import React from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * TrialExpiredBanner - Non-blocking upgrade reminder
 *
 * Shows at top of pages when trial expires but user still has read-only access
 * Professional, not aggressive - follows SaaS best practices
 */
export default function TrialExpiredBanner() {
  const { isActive, isTrialActive, trialEnded, daysLeft } = useSubscription();
  const { userDoc } = useAuth();

  // Don't show if user has active subscription
  if (isActive) return null;

  // Don't show if trial is still active
  if (isTrialActive) return null;

  // Don't show if trial hasn't started/expired yet
  if (!trialEnded) return null;

  // Don't show for non-teachers
  if (userDoc?.role !== 'teacher') return null;

  const trialEndDate = userDoc?.trialEndsAt?.toDate
    ? userDoc.trialEndsAt.toDate()
    : userDoc?.trialEndsAt
    ? new Date(userDoc.trialEndsAt)
    : null;

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center flex-1">
            <span className="flex p-2 rounded-lg bg-white bg-opacity-20">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <p className="ml-3 font-medium text-sm sm:text-base">
              <span className="md:hidden">
                Trial Expired - Upgrade to create content
              </span>
              <span className="hidden md:inline">
                Your trial expired {trialEndDate ? `on ${trialEndDate.toLocaleDateString()}` : 'recently'}.
                <span className="font-semibold ml-1">Your data is safe.</span> Upgrade to create new quizzes and students.
              </span>
            </p>
          </div>
          <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
            <Link
              to="/billing"
              className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-amber-600 bg-white hover:bg-amber-50 transition-colors duration-200"
            >
              Upgrade Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact version for tight spaces (e.g., modals, sidebars)
 */
export function TrialExpiredBannerCompact() {
  const { isActive, trialEnded } = useSubscription();

  if (isActive || !trialEnded) return null;

  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-amber-700">
            Trial expired. <Link to="/billing" className="font-medium underline hover:text-amber-800">Upgrade</Link> to continue.
          </p>
        </div>
      </div>
    </div>
  );
}
